import React from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { VideoPlatformList } from "./VideoPlatformList";
import { MeetingDetailsDialog } from "./MeetingDetailsDialog";
import { DeleteMeetingDialog } from "./DeleteMeetingDialog";
import { CreateMeetingDialog } from "./CreateMeetingDialog";
import { useVideoPlatformStore } from "../../store/videoPlatformStore";
import { useVideoPlatformApi } from "./hooks/useVideoPlatformApi";
import { CreateMeetingFormData } from "./types";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useDialogState } from "../../hooks/useDialogState";

export const Management: React.FC = () => {
  const detailsDialog = useDialogState<string>();
  const deleteDialog = useDialogState<{ id: string; title: string }>();
  const createDialog = useDialogState();
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const { meetings, loading, loadMeetings } = useVideoPlatformStore();
  const api = useVideoPlatformApi();
  const { withLoading } = useGlobalLoading();

  // Only block the whole screen on the first load. Status-driven refetches keep
  // the grid mounted (and its filter dropdown state) while new rows stream in.
  const showInitialSpinner = loading && meetings.length === 0;

  React.useEffect(() => {
    loadMeetings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedMeeting = React.useMemo(() => {
    if (!detailsDialog.data) return null;
    return meetings.find((m) => m.id === detailsDialog.data) || null;
  }, [detailsDialog.data, meetings]);

  const handleView = (meetingId: string) => {
    detailsDialog.openWith(meetingId);
  };

  const handleDelete = (meetingId: string) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    if (meeting) {
      deleteDialog.openWith({ id: meeting.id, title: meeting.title });
    }
  };

  const handleCloseDialog = () => {
    detailsDialog.close();
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.data) return;

    try {
      await withLoading(api.deleteMeeting(deleteDialog.data.id));

      showSnackbar("Meeting ended successfully", "success");
      deleteDialog.close();
      loadMeetings();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to end meeting";
      showSnackbar(errorMessage, "error");
    }
  };

  const handleCloseDeleteDialog = () => {
    deleteDialog.close();
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadMeetings()
      .then(() => {
        showSnackbar("Meetings refreshed successfully", "success");
      })
      .catch((error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to refresh";
        showSnackbar(errorMessage, "error");
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  const handleStartNewMeeting = () => {
    createDialog.openDialog();
  };

  const handleCreateMeeting = async (formData: CreateMeetingFormData) => {
    try {
      await withLoading(api.createMeeting(formData));

      showSnackbar("Meeting created successfully", "success");
      createDialog.close();
      loadMeetings();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create meeting";
      showSnackbar(errorMessage, "error");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>
        Video Platform Meetings
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Manage video platform meetings
      </Typography>
      <Box sx={{ mt: 3 }}>
        {showInitialSpinner && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {!showInitialSpinner && (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <IconButton
                onClick={handleRefresh}
                disabled={isRefreshing}
                color="primary"
                sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}
              >
                <RefreshIcon />
              </IconButton>
              <Button
                variant="contained"
                onClick={handleStartNewMeeting}
                sx={{
                  backgroundColor: "#7357FF",
                  "&:hover": { backgroundColor: "#5a42cc" },
                }}
              >
                Start a new meeting
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  localStorage.setItem("activeScreenId", "moderation");
                  window.location.reload();
                }}
                sx={{
                  borderColor: "#7357FF",
                  color: "#7357FF",
                  "&:hover": {
                    borderColor: "#5a42cc",
                    backgroundColor: "rgba(115, 87, 255, 0.04)",
                  },
                }}
              >
                Moderation
              </Button>
            </Box>

            <VideoPlatformList onView={handleView} onDelete={handleDelete} />

            <MeetingDetailsDialog
              open={detailsDialog.open}
              meeting={selectedMeeting}
              onClose={handleCloseDialog}
            />

            <DeleteMeetingDialog
              open={deleteDialog.open}
              meetingTitle={deleteDialog.data?.title || ""}
              onClose={handleCloseDeleteDialog}
              onConfirm={handleConfirmDelete}
            />

            <CreateMeetingDialog
              open={createDialog.open}
              onClose={() => createDialog.close()}
              onSubmit={handleCreateMeeting}
            />
          </>
        )}

        {SnackbarNode}
      </Box>
    </Box>
  );
};
